/**
 * DOK Auto-Tagger — Heuristic DOK level detection for questions.
 *
 * PEDAGOGICAL_CONTRACT_SPEC §9 Fase 3 — Auto-tags existing seeds with
 * best-guess DOK levels based on question characteristics. Results should
 * be reviewed by content authors via the governance admin UI.
 *
 * DOK Levels (Webb's Depth of Knowledge, adapted for Dutch primary):
 * 1 = Recall/Reproduce — direct fact retrieval, simple computation
 * 2 = Skill/Concept — apply a learned procedure, compare, classify
 * 3 = Strategic Thinking — multi-step reasoning, justify, plan
 * 4 = Extended Thinking — investigate, create, synthesize
 *
 * This is a HEURISTIC — it will be wrong for some questions.
 * The governance pipeline flags auto-tagged DOK for human review.
 */

import type { Question } from '../../content'
import type { DOKLevel } from '../../content/types'
import { QUESTION_TYPE_REGISTRY } from '../questionTypeDefinitions'
import {
  getMinDokForBand,
  getModuleCognitiveProfiles,
  getTierBand,
} from '../tierBands'

export interface DokEstimate {
  dok: DOKLevel
  confidence: 'high' | 'medium' | 'low'
  reason: string
}

/**
 * GSR-17 (audit 2026): map the per-type `cognitiveCeiling` sidecar value
 * ('DOK1' | 'DOK2' | 'DOK3') to a numeric DOKLevel for clamping. Used by
 * estimateDok() to ensure heuristic output never exceeds the structural
 * ceiling defined in the question-type sidecar (e.g. a step_solver with
 * trivial content should not auto-tag as DOK4 — its ceiling is DOK3).
 */
const CEILING_TO_DOK: Record<'DOK1' | 'DOK2' | 'DOK3', DOKLevel> = {
  DOK1: 1,
  DOK2: 2,
  DOK3: 3,
}

// ─── Signal detectors ─────────────────────────────────────────────────────────

/** Keywords that suggest higher-order thinking (Dutch) */
const STRATEGIC_KEYWORDS = [
  'strategie',
  'waarom',
  'verklaar',
  'beredeneer',
  'bewijs',
  'analyseer',
  'vergelijk en verklaar',
  'welke aanpak',
  'meerdere manieren',
  'meerstaps',
  'combineer',
  'ontwerp',
  'bedenk',
  'onderzoek',
  'evalueer',
  'beoordeel',
]

const RECALL_KEYWORDS = [
  'wat is',
  'hoeveel is',
  'noem',
  'welk',
  'hoe heet',
  'waar ligt',
  'reken uit',
  'bereken',
]

const CONCEPT_KEYWORDS = [
  'vergelijk',
  'sorteer',
  'classificeer',
  'welke regel',
  'pas toe',
  'los op',
  'vul aan',
  'welke breuk',
  'zet om',
  'rond af',
]

function hasKeywords(text: string, keywords: string[]): number {
  const lower = text.toLowerCase()
  return keywords.filter((kw) => lower.includes(kw)).length
}

// ─── Question type signals ────────────────────────────────────────────────────

/** Question types that are inherently DOK 1 (recall) */
const DOK1_TYPES = new Set([
  'true_false_series',
  'clock', // reading a clock = recall
])

/** Question types that are inherently DOK 2+ (application) */
const DOK2_TYPES = new Set([
  'clock_set',
  'categorize',
  'ordering',
  'matching',
  'error_spotting',
  'sentence_parser',
  'comparison_judge',
  'verb_conjugation_flow',
])

/**
 * Question types that suggest DOK 3 (strategic).
 *
 * ⚠️ Known drift (GOVERNANCE_DETANGLE_PLAN_2026 §16.2): `strategy_solver` is
 * placed at the `strategic` floor by the structural SSOT
 * `shared/proxyContracts/cognitiveSignals.ts` (TYPE_FLOOR) but is MISSING
 * here, so `estimateDok()` under-tags untagged strategy_solver seeds.
 * Adding it shifts heuristic output → can change `SEED_DOK_MISSING` metadata
 * and seedConsolidator auto-fill, so this is behavior-changing and is
 * shadow-gated under the FG5 regression batch — NOT applied unilaterally.
 */
const DOK3_TYPES = new Set(['step_solver'])

// ─── Main estimator ──────────────────────────────────────────────────────────

/**
 * Estimate the DOK level of a question based on heuristics.
 *
 * Factors considered:
 * 1. Question type (some types are inherently higher/lower DOK)
 * 2. Keywords in question text (strategic vs. recall language)
 * 3. Tier level (higher tiers tend to require higher DOK)
 * 4. Number of steps/complexity signals (context, multi-part)
 * 5. Presence of context paragraph (suggests application)
 */
export function estimateDok(question: Question): DokEstimate {
  const text = question.question ?? ''
  const context = question.context ?? ''
  const tier = question.tier ?? 1
  const type = question.questionType

  let dokScore = 0
  const reasons: string[] = []

  // 1. Question type baseline
  if (DOK3_TYPES.has(type)) {
    dokScore += 3
    reasons.push(`type "${type}" suggests strategic thinking`)
  } else if (DOK2_TYPES.has(type)) {
    dokScore += 2
    reasons.push(`type "${type}" requires concept application`)
  } else if (DOK1_TYPES.has(type)) {
    dokScore += 1
    reasons.push(`type "${type}" is typically recall`)
  } else {
    dokScore += 1.5 // neutral baseline
  }

  // 2. Keyword analysis
  const strategicHits = hasKeywords(text + ' ' + context, STRATEGIC_KEYWORDS)
  const recallHits = hasKeywords(text, RECALL_KEYWORDS)
  const conceptHits = hasKeywords(text + ' ' + context, CONCEPT_KEYWORDS)

  if (strategicHits >= 2) {
    dokScore += 1.5
    reasons.push(`${strategicHits} strategic keywords`)
  } else if (strategicHits === 1) {
    dokScore += 0.75
    reasons.push('1 strategic keyword')
  }

  if (conceptHits >= 2) {
    dokScore += 0.5
    reasons.push(`${conceptHits} concept keywords`)
  }

  if (recallHits >= 2 && strategicHits === 0) {
    dokScore -= 0.5
    reasons.push(`${recallHits} recall keywords, no strategic`)
  }

  // 3. Context paragraph suggests application (DOK 2+)
  if (context && context.length > 50) {
    dokScore += 0.5
    reasons.push('has context paragraph (application)')
  }

  // 4. Tier influence (gentle — tier is difficulty, not DOK, but correlated)
  if (tier >= 13) {
    dokScore += 0.5
    reasons.push('expert tier (13+)')
  } else if (tier >= 9) {
    dokScore += 0.25
    reasons.push('advanced tier (9-12)')
  }

  // 5. Multi-step signals
  if (
    text.includes('stap') ||
    text.includes('eerst') ||
    text.includes('daarna')
  ) {
    dokScore += 0.5
    reasons.push('multi-step language detected')
  }

  // Convert score to DOK level
  let dok: DOKLevel
  if (dokScore >= 3.5) {
    dok = 4
  } else if (dokScore >= 2.5) {
    dok = 3
  } else if (dokScore >= 1.5) {
    dok = 2
  } else {
    dok = 1
  }

  // GSR-17 (audit 2026): clamp the heuristic output to the per-type
  // `cognitiveCeiling` from the governance sidecar. Without this clamp a
  // step_solver with simple content could end up auto-tagged DOK4, which
  // exceeds the type's structural ceiling (DOK3). `DOK*_TYPES` sets encode
  // BASELINE signal; `cognitiveCeiling` encodes the MAXIMUM — they are
  // compatible different facts (baseline ≤ ceiling). The clamp is the
  // missing link that prevents over-tagging.
  // Safe default: when a type lacks sidecar metadata the registry builder
  // already fills in DOK2 ceiling — so the lookup is always defined.
  const ceiling = QUESTION_TYPE_REGISTRY[type]?.cognitiveCeiling
  const ceilingDok = ceiling ? CEILING_TO_DOK[ceiling] : 4
  if (dok > ceilingDok) {
    reasons.push(`clamped from DOK${dok} to ${ceiling} (type ceiling)`)
    dok = ceilingDok
  }

  // S1 (GET_SSOT_DOK_GOVERNANCE_AUDIT_2026-06 §3.4): module-cognitive-profile
  // clamp. The heuristic above is text/type-driven and PROFILE-BLIND — it
  // over-tags recall families (Topo capitals/rivers, loanword spelling) at
  // DOK 2 purely from the tier-bonus, which collides with the
  // `recall-knowledge` floor in `tierBands.getMinDokForBand` ("recall stays
  // recall, even on E8+"). Clamping the estimate UP to the module's
  // profile-aware band floor makes the estimate both *correct* (a recall
  // family resolves to DOK 1) and a high-signal structural fact — so it is
  // no longer noise. Mirrors the existing per-type `cognitiveCeiling` clamp:
  // floor <= estimate <= ceiling. Only applies when module context is known
  // (auto-fill path passes it; legacy callers without `module` are unchanged).
  let profileFloorApplied = false
  const module = question.module
  if (module) {
    const profile = getModuleCognitiveProfiles()[module]
    const band = getTierBand(tier)
    const profileFloor = getMinDokForBand(band, module, question.skill)
    if (profile && profile !== 'procedural-math') {
      // Non-procedural profiles (recall-knowledge, comprehension,
      // application-language) have a FIXED cognitive level per band — recall
      // is DOK 1 even on E8+, study-strategy/language top out at DOK 2. For
      // these the band value acts as both floor AND ceiling: snap the
      // text-driven estimate to it (capped by the per-type ceiling). This is
      // what stops the tier-bonus from over-tagging a Topo recall question
      // at DOK 2 — the dominant SEED_DOK_MISSING noise in the report.
      const snapped = Math.min(profileFloor, ceilingDok) as DOKLevel
      if (snapped !== dok) {
        reasons.push(
          `snapped from DOK${dok} to DOK${snapped} (${profile} profile for ${module} @ ${band})`,
        )
        dok = snapped
      }
      profileFloorApplied = true
    } else if (profileFloor > dok) {
      // procedural-math (or unclassified): the band value is a FLOOR only —
      // raise an under-tagged estimate, never lower a legitimately higher one.
      reasons.push(
        `raised from DOK${dok} to DOK${profileFloor} (module-profile floor for ${module} @ ${band})`,
      )
      dok = Math.min(profileFloor, ceilingDok) as DOKLevel
      profileFloorApplied = true
    }
  }

  // Confidence based on signal strength. The module-profile floor is a
  // STRUCTURAL signal (not a fuzzy keyword guess), so when it sets the floor
  // we treat the estimate as at least medium confidence — this lets
  // `seedConsolidator.autoFillDok` silently fill recall/comprehension seeds
  // that would otherwise linger as low-confidence SEED_DOK_MISSING noise.
  const signalCount = reasons.length
  let confidence: 'high' | 'medium' | 'low'
  if (signalCount >= 3 && (strategicHits >= 2 || DOK3_TYPES.has(type))) {
    confidence = 'high'
  } else if (signalCount >= 2 || profileFloorApplied) {
    confidence = 'medium'
  } else {
    confidence = 'low'
  }

  return {
    dok,
    confidence,
    reason: reasons.join('; '),
  }
}
